import React, { useEffect, useMemo, useState } from "react";
import LandingNav from "../../components/LandingNav";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import CallReceivedOutlinedIcon from "@mui/icons-material/CallReceivedOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import FlightOutlinedIcon from "@mui/icons-material/FlightOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import BalconyOutlinedIcon from "@mui/icons-material/BalconyOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import type { SvgIconComponent } from "@mui/icons-material";

// ── Rates (INR) ──────────────────────────────────────────────
const RATE_RENDER_POINT = 5000;
const SQFT_PER_POINT_INTERIOR = 75;
const SQFT_PER_POINT_CLUBHOUSE = 500;
const POINTS_PER_ACRE = 10;
const RATE_SKYWALK_BASE = 300000;
const RATE_EXTRA_ROUTE_VIEW = 100000;
const RATE_BALCONY_VIEW = 2000;
const PLATFORM_ANNUAL = 240000;

// Format a number as Indian Rupees (₹1,23,456)
const formatINR = (n: number): string =>
  "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });

// ── Platform features ────────────────────────────────────────
const PLATFORM_FEATURES: { icon: SvgIconComponent; title: string; detail: string }[] = [
  {
    icon: AdminPanelSettingsOutlinedIcon,
    title: "Enterprise Business Admin login",
    detail: "Top-level access to manage your entire OpenSky organisation.",
  },
  {
    icon: GroupsOutlinedIcon,
    title: "Up to 5 System Admin seats",
    detail:
      "Delegate operations to your senior team without sharing the primary login.",
  },
  {
    icon: HandshakeOutlinedIcon,
    title: "Unlimited agents & channel partners",
    detail:
      "Onboard your in-house team and external channel partners — no per-seat fees.",
  },
  {
    icon: InboxOutlinedIcon,
    title: "Unlimited leads & customer records",
    detail:
      "Every walkthrough visitor, every inquiry, every conversion — all captured.",
  },
  {
    icon: CodeOutlinedIcon,
    title: "White-label experience embed",
    detail:
      "Host the OpenSky experience inside your own developer website via iframe.",
  },
  {
    icon: CallReceivedOutlinedIcon,
    title: "Lead capture from embedded experiences",
    detail:
      "Leads from your branded iframe flow back into the same enterprise dashboard.",
  },
  {
    icon: InsightsOutlinedIcon,
    title: "Full analytics dashboard",
    detail:
      "Session-level analytics on visitor behavior, engagement and conversion.",
  },
  {
    icon: LayersOutlinedIcon,
    title: "Multi-project management",
    detail:
      "Manage every project — built or unbuilt — from one consolidated workspace.",
  },
];

// ── Rate card rows ───────────────────────────────────────────
const RATE_CARD: { icon: SvgIconComponent; service: string; rate: string; sizing: string }[] = [
  {
    icon: MeetingRoomOutlinedIcon,
    service: "Metamodel — interior renders",
    rate: `${formatINR(RATE_RENDER_POINT)} / render point`,
    sizing:
      "1 point per 75 sqft (flats / villas with interiors) · 1 point per 500 sqft (clubhouse)",
  },
  {
    icon: DirectionsWalkOutlinedIcon,
    service: "Walkthroughs",
    rate: `${formatINR(RATE_RENDER_POINT)} / render point`,
    sizing: "10 points per acre of site area",
  },
  {
    icon: FlightOutlinedIcon,
    service: "Skywalk + 2 route views (base package)",
    rate: `${formatINR(RATE_SKYWALK_BASE)} / project`,
    sizing: "4 zones within 8–10 km radius + 2 route views included",
  },
  {
    icon: RouteOutlinedIcon,
    service: "Additional route view",
    rate: `${formatINR(RATE_EXTRA_ROUTE_VIEW)} each`,
    sizing: "Beyond the 2 included in the skywalk package",
  },
  {
    icon: BalconyOutlinedIcon,
    service: "Balcony views",
    rate: `${formatINR(RATE_BALCONY_VIEW)} each`,
    sizing: "Per balcony, photoreal still",
  },
  {
    icon: UpdateOutlinedIcon,
    service: "Quarterly project updates",
    rate: "Scope-based",
    sizing: "Re-scans / re-renders every 3 months — priced per change request",
  },
];

// ── FAQ ──────────────────────────────────────────────────────
const FAQ: { q: string; a: string }[] = [
  {
    q: "What's a render point?",
    a: "A render point is a single photoreal viewpoint produced by our content team. For interiors we recommend one render point per ~75 sqft for flats/villas and one per ~500 sqft for clubhouses. For walkthroughs we recommend ten points per acre. Each point is priced at ₹5,000.",
  },
  {
    q: "Do leads from the embed land in my OpenSky dashboard?",
    a: "Yes. Any lead captured through the OpenSky experience — whether on opensky.com or embedded inside your own developer website via iframe — is written to the same enterprise inbox visible to your Business Admin and System Admin seats.",
  },
  {
    q: "Can I keep my own branding on the embedded experience?",
    a: "Yes. The iframe embed is white-label-friendly. Your URL stays your URL; visitors never leave your domain. Optional 'Powered by OpenSky' attribution is configurable.",
  },
  {
    q: "What does the annual platform subscription not include?",
    a: "Content creation (metamodels, walkthroughs, skywalks, balcony views, quarterly updates) is billed separately per project against the rate card above. The platform fee covers software access, user seats, lead inbox, analytics and the embed feature.",
  },
  {
    q: "Can I add more channel partners later?",
    a: "Yes — channel partner and agent seats are unlimited under the annual subscription. Onboard or revoke access from the Business Admin dashboard at any time.",
  },
];

// ── Calculator ───────────────────────────────────────────────
interface CalcInputs {
  interiorSqft: number;
  clubhouseSqft: number;
  acres: number;
  balconies: number;
  includeSkywalk: boolean;
  extraRouteViews: number;
}

interface LineItem {
  label: string;
  detail: string;
  cost: number;
}

const computeLineItems = (i: CalcInputs): LineItem[] => {
  const items: LineItem[] = [];

  const interiorPoints = Math.ceil(i.interiorSqft / SQFT_PER_POINT_INTERIOR);
  if (interiorPoints > 0) {
    items.push({
      label: "Metamodel — interiors",
      detail: `${interiorPoints} render points × ${formatINR(RATE_RENDER_POINT)} (${i.interiorSqft.toLocaleString("en-IN")} sqft ÷ 75)`,
      cost: interiorPoints * RATE_RENDER_POINT,
    });
  }

  const clubhousePoints = Math.ceil(i.clubhouseSqft / SQFT_PER_POINT_CLUBHOUSE);
  if (clubhousePoints > 0) {
    items.push({
      label: "Metamodel — clubhouse",
      detail: `${clubhousePoints} render points × ${formatINR(RATE_RENDER_POINT)} (${i.clubhouseSqft.toLocaleString("en-IN")} sqft ÷ 500)`,
      cost: clubhousePoints * RATE_RENDER_POINT,
    });
  }

  const walkthroughPoints = Math.ceil(i.acres * POINTS_PER_ACRE);
  if (walkthroughPoints > 0) {
    items.push({
      label: "Walkthroughs",
      detail: `${walkthroughPoints} render points × ${formatINR(RATE_RENDER_POINT)} (${i.acres} acres × 10)`,
      cost: walkthroughPoints * RATE_RENDER_POINT,
    });
  }

  if (i.includeSkywalk) {
    items.push({
      label: "Skywalk + 2 route views",
      detail: "Base package — 4 zones + 2 route views",
      cost: RATE_SKYWALK_BASE,
    });
  }

  if (i.extraRouteViews > 0) {
    items.push({
      label: "Additional route views",
      detail: `${i.extraRouteViews} × ${formatINR(RATE_EXTRA_ROUTE_VIEW)}`,
      cost: i.extraRouteViews * RATE_EXTRA_ROUTE_VIEW,
    });
  }

  if (i.balconies > 0) {
    items.push({
      label: "Balcony views",
      detail: `${i.balconies} × ${formatINR(RATE_BALCONY_VIEW)}`,
      cost: i.balconies * RATE_BALCONY_VIEW,
    });
  }

  return items;
};

// ── Reusable: number input with display state ────────────────
const NumberInput: React.FC<{
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}> = ({ label, hint, value, onChange, min = 0, max, step = 1 }) => {
  const [raw, setRaw] = useState<string>(value === 0 ? "" : String(value));

  return (
    <label className="flex flex-col gap-1">
      <span className="text-white text-sm font-medium">{label}</span>
      {hint && <span className="text-white/50 text-xs">{hint}</span>}
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={raw}
        onChange={(e) => {
          const s = e.target.value;
          if (s === "") {
            setRaw("");
            onChange(0);
            return;
          }
          const n = parseFloat(s);
          if (!Number.isFinite(n)) return;
          let clamped = Math.max(min, n);
          if (max !== undefined) clamped = Math.min(max, clamped);
          setRaw(clamped === n ? s : String(clamped));
          onChange(clamped);
        }}
        className="mt-1 bg-white/[0.07] border border-white/15 rounded-[8px] px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#b7aca2]/80 focus:bg-white/10 transition-colors"
      />
    </label>
  );
};

// ── Reusable: section eyebrow ───────────────────────────────
const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    className="text-[#b7aca2] text-xs uppercase tracking-[0.4em] mb-4"
    style={{ fontFamily: "'DM Sans', sans-serif" }}
  >
    {children}
  </p>
);

// ── FAQ item with disclosure ────────────────────────────────
const FAQItem: React.FC<{ q: string; a: string; defaultOpen?: boolean }> = ({
  q,
  a,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
        aria-expanded={open}
      >
        <span className="text-white text-base font-medium group-hover:text-[#b7aca2] transition-colors">
          {q}
        </span>
        <span
          className={`text-[#b7aca2] text-xl transition-transform duration-200 ${
            open ? "rotate-45" : "rotate-0"
          }`}
          aria-hidden="true"
        >
          +
        </span>
      </button>
      {open && (
        <div className="pb-5 text-white/60 text-sm leading-relaxed pr-8">{a}</div>
      )}
    </div>
  );
};

export const Pricing: React.FC = () => {
  // index.html sets body { overflow: hidden } globally for the authenticated app.
  // Marketing pages need the body to scroll — restore it on mount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const [inputs, setInputs] = useState<CalcInputs>({
    interiorSqft: 75000,
    clubhouseSqft: 5000,
    acres: 2,
    balconies: 20,
    includeSkywalk: true,
    extraRouteViews: 0,
  });

  const lineItems = useMemo(() => computeLineItems(inputs), [inputs]);
  const subtotal = useMemo(
    () => lineItems.reduce((s, li) => s + li.cost, 0),
    [lineItems]
  );

  const set =
    <K extends keyof CalcInputs>(k: K) =>
    (v: CalcInputs[K]) =>
      setInputs((prev) => ({ ...prev, [k]: v }));

  return (
    <div
      className="bg-[#0d0d0d] min-h-screen"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <LandingNav active="pricing" />

      {/* ── HERO ───────────────────────────────────────────── */}
      <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Ambient gradient orbs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-24 left-1/3 w-[40rem] h-[40rem] bg-[#b7aca2]/[0.08] blur-[140px] rounded-full"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-48 right-1/4 w-[36rem] h-[36rem] bg-[#6366f1]/[0.06] blur-[140px] rounded-full"
        />

        <div className="relative max-w-4xl mx-auto text-center">
          <Eyebrow>Pricing</Eyebrow>
          <h1
            className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-[1.05] tracking-tight"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Built for builders.
            <br />
            <span className="bg-gradient-to-r from-[#b7aca2] via-white to-[#b7aca2] bg-clip-text text-transparent">
              Priced per project.
            </span>
          </h1>
          <p className="text-white/65 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10 sm:mb-12">
            One annual platform subscription gives your entire organisation access.
            Content is created project-by-project against a transparent rate card. No
            surprise invoices, ever.
          </p>

          {/* Stat row */}
          <div className="grid grid-cols-3 gap-3 sm:gap-6 md:gap-12 max-w-3xl mx-auto pt-8 border-t border-white/10">
            {[
              {
                Icon: GroupsOutlinedIcon,
                stat: "Unlimited",
                label: "agents & channel partners",
              },
              {
                Icon: MarkEmailReadOutlinedIcon,
                stat: "One",
                label: "inbox for every lead",
              },
              {
                Icon: ReceiptLongOutlinedIcon,
                stat: "100%",
                label: "transparent rate card",
              },
            ].map(({ Icon, stat, label }) => (
              <div key={label} className="flex flex-col items-center text-center">
                <Icon
                  sx={{ fontSize: 24, color: "#b7aca2" }}
                  className="mb-2 sm:mb-3 opacity-80 sm:!text-[28px]"
                />
                <div
                  className="text-white text-base sm:text-2xl md:text-3xl font-bold leading-tight"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {stat}
                </div>
                <div className="text-white/50 text-[10px] sm:text-xs md:text-sm mt-1 leading-snug">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM SUBSCRIPTION ──────────────────────────── */}
      <section className="px-4 sm:px-6 pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl sm:rounded-3xl border border-[#b7aca2]/25 bg-gradient-to-br from-[#1e1e1e] via-[#181818] to-[#141414] p-6 sm:p-10 lg:p-14 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] overflow-hidden">
            {/* corner accent */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 bg-[#b7aca2]/10 blur-3xl rounded-full"
            />

            <div className="relative">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 sm:gap-8 mb-8 sm:mb-10">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#b7aca2]/15 border border-[#b7aca2]/30 mb-4 sm:mb-5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#b7aca2] animate-pulse" />
                    <span className="text-[#b7aca2] text-[10px] sm:text-xs uppercase tracking-[0.25em] font-medium">
                      Platform subscription
                    </span>
                  </div>
                  <h2
                    className="text-white text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-2 sm:mb-3"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    OpenSky Platform Access
                  </h2>
                  <p className="text-white/55 text-sm sm:text-base">
                    Annual subscription · everything you need to operate
                  </p>
                </div>
                <div className="shrink-0">
                  <div className="flex items-baseline gap-2 justify-start sm:justify-end">
                    <span
                      className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {formatINR(PLATFORM_ANNUAL)}
                    </span>
                  </div>
                  <p className="text-white/40 text-xs sm:text-sm sm:text-right mt-1">
                    billed annually · GST extra
                  </p>
                </div>
              </div>

              <div className="border-t border-white/10 pt-8 sm:pt-10 mb-8 sm:mb-10">
                <p
                  className="text-[#b7aca2] text-xs uppercase tracking-[0.3em] mb-5 sm:mb-6"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  What's included
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-10 gap-y-5 sm:gap-y-6">
                  {PLATFORM_FEATURES.map((f) => {
                    const Icon = f.icon;
                    return (
                      <li key={f.title} className="flex items-start gap-3">
                        <span
                          className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#b7aca2]/12 border border-[#b7aca2]/25 text-[#b7aca2]"
                          aria-hidden="true"
                        >
                          <Icon sx={{ fontSize: 18 }} />
                        </span>
                        <div>
                          <div className="text-white text-sm font-medium">
                            {f.title}
                          </div>
                          <div className="text-white/50 text-xs mt-0.5 leading-relaxed">
                            {f.detail}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href="/builders#inquiry"
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-10 py-3.5 rounded-md text-sm font-semibold text-[#1a1a1a] bg-[#b7aca2] hover:bg-white transition-colors"
                >
                  Get Platform Access
                </a>
                <a
                  href="#calculator"
                  className="flex-1 sm:flex-none inline-flex items-center justify-center px-10 py-3.5 rounded-md text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors"
                >
                  Estimate a project →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── EMBED ON YOUR WEBSITE ──────────────────────────── */}
      <section className="bg-gradient-to-b from-[#0d0d0d] via-[#111] to-[#0d0d0d] py-16 sm:py-20 lg:py-24 px-4 sm:px-6 border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <Eyebrow>Embed anywhere</Eyebrow>
              <h2
                className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-5 sm:mb-6 leading-[1.1]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Host the experience on your own website.
              </h2>
              <p className="text-white/65 text-base md:text-lg leading-relaxed mb-7 sm:mb-8">
                Drop a single iframe into your developer site and the full OpenSky
                experience — digital twins, route views, walkthroughs, balcony stills —
                renders inline under your brand. Visitors never leave your domain.
              </p>
              <ul className="space-y-4 mb-10">
                {[
                  {
                    Icon: LanguageOutlinedIcon,
                    title: "Stay on your domain",
                    detail:
                      "The iframe loads inside your developer website. Your URL, your branding, your conversion flow.",
                  },
                  {
                    Icon: MoveToInboxOutlinedIcon,
                    title: "Leads land in your enterprise dashboard",
                    detail:
                      "Every form submission, every walkthrough handoff — captured in the same OpenSky inbox your Business Admin already monitors.",
                  },
                  {
                    Icon: HubOutlinedIcon,
                    title: "One source of truth",
                    detail:
                      "Whether a prospect arrived via opensky.com, your website, or a channel partner — the lead lives in one place.",
                  },
                ].map(({ Icon, title, detail }) => (
                  <li key={title} className="flex items-start gap-3">
                    <span
                      className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#b7aca2]/12 border border-[#b7aca2]/25 text-[#b7aca2]"
                      aria-hidden="true"
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </span>
                    <div>
                      <div className="text-white text-sm font-semibold">{title}</div>
                      <div className="text-white/55 text-sm mt-1 leading-relaxed">
                        {detail}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <a
                href="/builders#inquiry"
                className="inline-flex items-center gap-2 text-[#b7aca2] text-sm font-semibold hover:text-white transition-colors group"
              >
                Talk to us about embedding
                <span
                  className="inline-block transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                >
                  →
                </span>
              </a>
            </div>

            {/* Mock browser frame */}
            <div className="relative">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-6 bg-[#b7aca2]/5 blur-3xl rounded-3xl"
              />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#0a0a0a]">
                {/* Browser chrome */}
                <div className="bg-[#0f0f0f] px-4 py-3 flex items-center gap-3 border-b border-white/10">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500/50" />
                    <span className="w-3 h-3 rounded-full bg-yellow-500/50" />
                    <span className="w-3 h-3 rounded-full bg-green-500/50" />
                  </div>
                  <div className="flex-1 ml-3 px-3 py-1 rounded-md bg-white/[0.08] text-white/50 text-xs font-mono truncate">
                    yourdeveloper.com/projects/skyline-residences
                  </div>
                </div>
                {/* Page content */}
                <div className="px-6 pt-6 pb-2 text-white/70 text-xs">
                  Skyline Residences — Sector 47, Gurugram
                </div>
                {/* Embedded iframe area */}
                <div className="mx-6 mb-6 rounded-lg border border-[#b7aca2]/30 bg-gradient-to-br from-[#1a1a1a] via-[#1f1f1f] to-[#141414] aspect-[16/10] flex flex-col items-center justify-center relative overflow-hidden">
                  {/* faux 3D scene shimmer */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(183,172,162,0.18),transparent_60%)]"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(99,102,241,0.10),transparent_55%)]"
                  />
                  <div className="relative text-center px-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 mb-4">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#b7aca2] animate-pulse" />
                      <span className="text-white/80 text-[10px] uppercase tracking-widest">
                        Live experience
                      </span>
                    </div>
                    <p className="text-white text-base sm:text-lg font-semibold mb-1">
                      Interactive Property Experience
                    </p>
                    <p className="text-white/50 text-xs">
                      Walkthrough · digital twin · route view
                    </p>
                  </div>
                  <div className="absolute bottom-3 right-3 text-white/30 text-[10px] font-mono">
                    Powered by OpenSky
                  </div>
                </div>
              </div>
              {/* Caption */}
              <p className="text-center text-white/40 text-xs mt-4 font-mono">
                &lt;iframe src="https://embed.opensky.com/p/skyline-residences"&gt;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTENT RATE CARD ──────────────────────────────── */}
      <section className="bg-[#0d0d0d] py-16 sm:py-20 lg:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-14 max-w-3xl mx-auto">
            <Eyebrow>Content creation</Eyebrow>
            <h2
              className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-tight"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Pay per project, by clear rates.
            </h2>
            <p className="text-white/60 text-sm sm:text-base">
              Every piece of content we create is priced against the table below. Use the
              calculator below it to estimate any project, instantly.
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#161616] to-[#0f0f0f]">
            <div className="hidden md:grid md:grid-cols-[1.4fr_1fr_2fr] bg-white/[0.04] text-[#b7aca2] text-xs uppercase tracking-[0.25em] px-7 py-4 border-b border-white/5">
              <div>Service</div>
              <div>Rate</div>
              <div>Sizing rule</div>
            </div>
            {RATE_CARD.map((row, i) => {
              const Icon = row.icon;
              return (
                <div
                  key={row.service}
                  className={`grid grid-cols-1 md:grid-cols-[1.4fr_1fr_2fr] gap-3 md:gap-6 px-5 sm:px-7 py-5 sm:py-6 hover:bg-white/[0.02] transition-colors ${
                    i !== RATE_CARD.length - 1 ? "border-b border-white/5" : ""
                  }`}
                >
                  <div className="flex items-center gap-3 text-white font-semibold text-sm">
                    <span
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#b7aca2]/10 border border-[#b7aca2]/20 text-[#b7aca2]"
                      aria-hidden="true"
                    >
                      <Icon sx={{ fontSize: 18 }} />
                    </span>
                    <span>{row.service}</span>
                  </div>
                  <div className="text-[#b7aca2] text-sm font-semibold md:self-center md:pl-0 pl-12">
                    <span className="md:hidden text-[#b7aca2]/60 text-[10px] uppercase tracking-[0.2em] block mb-0.5">Rate</span>
                    {row.rate}
                  </div>
                  <div className="text-white/55 text-sm leading-relaxed md:self-center md:pl-0 pl-12">
                    <span className="md:hidden text-[#b7aca2]/60 text-[10px] uppercase tracking-[0.2em] block mb-0.5">Sizing rule</span>
                    {row.sizing}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CALCULATOR ─────────────────────────────────────── */}
      <section
        id="calculator"
        className="bg-gradient-to-b from-[#0d0d0d] via-[#111] to-[#0d0d0d] py-16 sm:py-20 lg:py-24 px-4 sm:px-6 border-y border-white/5"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 max-w-3xl mx-auto">
            <Eyebrow>Live estimate</Eyebrow>
            <h2
              className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-tight"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Estimate your project in 30 seconds.
            </h2>
            <p className="text-white/60 text-sm sm:text-base">
              Enter your project's specs below. Numbers update live — this is an
              indicative quote; the final scope is confirmed during onboarding.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Inputs */}
            <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#161616] to-[#0f0f0f] p-5 sm:p-7 lg:p-9">
              <h3 className="text-white font-semibold text-lg mb-6">Your project</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <NumberInput
                  label="Interior sqft"
                  hint="Total flats / villas with interiors (max 10,00,000)"
                  value={inputs.interiorSqft}
                  onChange={set("interiorSqft")}
                  step={500}
                  max={1000000}
                />
                <NumberInput
                  label="Clubhouse sqft"
                  hint="Common areas & clubhouse (max 5,00,000)"
                  value={inputs.clubhouseSqft}
                  onChange={set("clubhouseSqft")}
                  step={250}
                  max={500000}
                />
                <NumberInput
                  label="Site area (acres)"
                  hint="For walkthroughs (max 500)"
                  value={inputs.acres}
                  onChange={set("acres")}
                  step={0.5}
                  max={500}
                />
                <NumberInput
                  label="Balcony views"
                  hint="One render per balcony (max 5,000)"
                  value={inputs.balconies}
                  onChange={set("balconies")}
                  max={5000}
                />
                <NumberInput
                  label="Extra route views"
                  hint="Beyond the 2 included in skywalk (max 5)"
                  value={inputs.extraRouteViews}
                  onChange={set("extraRouteViews")}
                  max={5}
                />
                <label className="flex items-start gap-3 cursor-pointer mt-1 select-none">
                  <input
                    type="checkbox"
                    checked={inputs.includeSkywalk}
                    onChange={(e) =>
                      set("includeSkywalk")(e.target.checked as unknown as boolean)
                    }
                    className="mt-1 h-4 w-4 accent-[#b7aca2]"
                  />
                  <span className="text-white text-sm">
                    <span className="font-medium">Include skywalk package</span>
                    <span className="block text-white/50 text-xs mt-0.5">
                      4 zones + 2 route views — {formatINR(RATE_SKYWALK_BASE)}
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* Breakdown */}
            <div className="relative rounded-2xl border border-[#b7aca2]/30 bg-gradient-to-br from-[#1e1e1e] via-[#181818] to-[#141414] p-5 sm:p-7 lg:p-9 flex flex-col shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-[#b7aca2]/10 blur-3xl rounded-full"
              />
              <div className="relative flex-1 flex flex-col">
                <h3 className="text-white font-semibold text-lg mb-6">
                  Estimated total
                </h3>

                {lineItems.length === 0 ? (
                  <p className="text-white/50 text-sm">
                    Enter your project's specs on the left to see a live estimate.
                  </p>
                ) : (
                  <div className="flex-1 flex flex-col">
                    <div className="space-y-4 mb-6 flex-1">
                      {lineItems.map((li) => (
                        <div
                          key={li.label}
                          className="flex items-start justify-between gap-4 border-b border-white/10 pb-3"
                        >
                          <div>
                            <div className="text-white text-sm font-medium">
                              {li.label}
                            </div>
                            <div className="text-white/50 text-xs mt-0.5">
                              {li.detail}
                            </div>
                          </div>
                          <div className="text-white text-sm font-semibold whitespace-nowrap">
                            {formatINR(li.cost)}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 border-t border-[#b7aca2]/40 pt-5 mt-auto">
                      <span className="text-[#b7aca2] text-[10px] sm:text-xs uppercase tracking-[0.3em]">
                        Estimated total
                      </span>
                      <span
                        className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {formatINR(subtotal)}
                      </span>
                    </div>
                    <p className="text-white/40 text-xs mt-3">
                      Excludes platform subscription ({formatINR(PLATFORM_ANNUAL)}/year)
                      and scope-based quarterly updates.
                    </p>
                  </div>
                )}

                <a
                  href="/builders#inquiry"
                  className="mt-7 inline-flex items-center justify-center w-full px-8 py-3.5 rounded-md text-sm font-semibold text-[#1a1a1a] bg-[#b7aca2] hover:bg-white transition-colors"
                >
                  Get a custom quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────── */}
      <section className="bg-[#0d0d0d] py-16 sm:py-20 lg:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <Eyebrow>FAQ</Eyebrow>
            <h2
              className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Common questions.
            </h2>
          </div>
          <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#161616] to-[#0f0f0f] px-5 sm:px-7 lg:px-10">
            {FAQ.map((item, i) => (
              <FAQItem key={item.q} q={item.q} a={item.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────── */}
      <section className="relative px-4 sm:px-6 py-16 sm:py-20 lg:py-24 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(183,172,162,0.12),transparent_65%)]"
        />
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#b7aca2]/15 border border-[#b7aca2]/30 mb-5 sm:mb-6">
            <RocketLaunchOutlinedIcon sx={{ fontSize: 28, color: "#b7aca2" }} />
          </div>
          <Eyebrow>Get started</Eyebrow>
          <h2
            className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-5 sm:mb-6 leading-tight"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Ready to scale your sales surface?
          </h2>
          <p className="text-white/65 text-base md:text-lg leading-relaxed mb-8 sm:mb-10">
            Talk to our team — we'll walk you through the platform, scope your first
            project against the rate card, and set up the embed for your developer
            website.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="/builders#inquiry"
              className="inline-flex items-center justify-center px-8 sm:px-10 py-3.5 rounded-md text-sm font-semibold text-[#1a1a1a] bg-[#b7aca2] hover:bg-white transition-colors"
            >
              Talk to us
            </a>
            <a
              href="/product"
              className="inline-flex items-center justify-center px-8 sm:px-10 py-3.5 rounded-md text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors"
            >
              Explore the product
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────── */}
      <footer className="bg-[#0a0a0a] py-10 sm:py-12 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-5 sm:gap-6">
          <img
            src="/images/logo.svg"
            alt="OpenSky"
            className="h-10 w-auto"
          />
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-white/60">
            <a href="/privacy_policy" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a
              href="/terms_and_conditions"
              className="hover:text-white transition-colors"
            >
              Terms & Conditions
            </a>
          </div>
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()}, OpenSky VR RE Platform
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
